import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, AlertTriangle, GitMerge } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useGoogleAdsCampaigns,
  type SyncAction,
  type SyncRow,
  type UnlinkedCampaign,
} from '@/hooks/useGoogleAdsCampaigns';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACTION_LABEL: Record<SyncAction, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  created: { label: 'New', variant: 'default' },
  adopted_by_id: { label: 'Linked (call id)', variant: 'default' },
  adopted_by_name: { label: 'Linked (name)', variant: 'default' },
  updated: { label: 'Up to date', variant: 'outline' },
  archived: { label: 'Archived', variant: 'secondary' },
};

function formatMicros(micros: number, currency: string | null): string {
  if (!micros) return '—';
  return new Intl.NumberFormat(undefined, {
    style: currency ? 'currency' : 'decimal',
    currency: currency ?? 'USD',
    maximumFractionDigits: 0,
  }).format(micros / 1_000_000);
}

/**
 * "Sync campaigns": pulls the connected Google Ads account and auto-maps one
 * local campaign per Google campaign (keyed on the Google id). Shows what
 * happened, then lets an admin fold any leftover unlinked local row into
 * the Google campaign it belongs to.
 */
export function GoogleAdsSyncDialog({ open, onOpenChange }: Props) {
  const {
    sync,
    isSyncing,
    syncResult,
    syncError,
    linkedCampaigns,
    unlinkedCampaigns,
    merge,
    isMerging,
  } = useGoogleAdsCampaigns();

  // Sync exactly once per opening. The ref stops the failed-mutation →
  // isSyncing-flip → effect-rerun loop that once hammered the function.
  const triggeredRef = useRef(false);
  useEffect(() => {
    if (!open) {
      triggeredRef.current = false;
      return;
    }
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    sync().catch(() => {/* surfaced via syncError */});
  }, [open, sync]);

  function retrySync() {
    triggeredRef.current = true;
    sync().catch(() => {/* surfaced via syncError */});
  }

  // Merge target picked per unlinked row; confirmation before the merge runs.
  const [targets, setTargets] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<UnlinkedCampaign | null>(null);

  const mergeTargets = useMemo(
    () => linkedCampaigns.filter((c) => c.google_ads_status !== 'REMOVED'),
    [linkedCampaigns],
  );
  const targetName = (id: string) => linkedCampaigns.find((c) => c.id === id)?.name ?? '';

  async function confirmMerge() {
    if (!pending) return;
    const into = targets[pending.id];
    if (!into) return;
    try {
      await merge({ from: pending.id, into });
      toast({ title: `Merged "${pending.name}" into "${targetName(into)}"` });
      setTargets((prev) => {
        const next = { ...prev };
        delete next[pending.id];
        return next;
      });
    } catch (e) {
      toast({
        title: 'Merge failed',
        description: e instanceof Error ? e.message.slice(0, 300) : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setPending(null);
    }
  }

  const rowsByGoogleId = useMemo(() => {
    const map = new Map<string, SyncRow>();
    for (const r of syncResult?.sync?.rows ?? []) map.set(r.google_ads_campaign_id, r);
    return map;
  }, [syncResult]);

  const summary = syncResult?.sync;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Sync Google Ads campaigns</DialogTitle>
            <DialogDescription>
              Every campaign in the connected account becomes a local campaign, keyed on its Google id.
              Calls carry that id from CTM, so leads and spend land on the same row. Removed campaigns are never imported.
            </DialogDescription>
          </DialogHeader>

          {isSyncing ? (
            <div className="flex items-center justify-center py-12 gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Pulling campaigns from Google Ads…
            </div>
          ) : syncError ? (
            <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">Couldn't sync campaigns</div>
                <div className="text-muted-foreground break-words">
                  {syncError instanceof Error ? syncError.message : 'Unknown error'}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={retrySync}>Retry</Button>
            </div>
          ) : !syncResult ? null : (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="default">{summary?.created ?? 0} new</Badge>
                <Badge variant="default">{(summary?.adopted_by_id ?? 0) + (summary?.adopted_by_name ?? 0)} linked</Badge>
                <Badge variant="outline">{summary?.updated ?? 0} up to date</Badge>
                <Badge variant="secondary">{summary?.archived ?? 0} archived</Badge>
                <span className="text-muted-foreground self-center">
                  {syncResult.customer_id} · 30-day spend in {syncResult.currency_code ?? 'account currency'}
                </span>
              </div>

              {syncResult.campaigns.length === 0 ? (
                <div className="rounded-lg border p-4 text-sm text-muted-foreground">
                  No active campaigns found in your Google Ads account ({syncResult.customer_id}).
                </div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium">Google Ads campaign</th>
                        <th className="px-3 py-2 font-medium">Local campaign</th>
                        <th className="px-3 py-2 font-medium w-[150px]">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {syncResult.campaigns.map((g) => {
                        const row = rowsByGoogleId.get(g.google_ads_campaign_id);
                        const action = row ? ACTION_LABEL[row.action] : null;
                        return (
                          <tr key={g.google_ads_campaign_id} className="border-t">
                            <td className="px-3 py-2 align-top">
                              <div className="font-medium">{g.name}</div>
                              <div className="text-xs text-muted-foreground flex gap-2 items-center mt-0.5">
                                <Badge variant={g.status === 'ENABLED' ? 'default' : 'secondary'} className="text-[10px]">
                                  {g.status}
                                </Badge>
                                <span>30d: {formatMicros(g.cost_micros_30d, syncResult.currency_code)}</span>
                                <span>· {g.clicks_30d.toLocaleString()} clicks</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              {row?.local_name ?? <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-3 py-2 align-top">
                              {action && <Badge variant={action.variant} className="text-xs">{action.label}</Badge>}
                            </td>
                          </tr>
                        );
                      })}
                      {(summary?.rows ?? []).filter((r) => r.action === 'archived').map((r) => (
                        <tr key={`archived-${r.campaign_id}`} className="border-t text-muted-foreground">
                          <td className="px-3 py-2 align-top">
                            <div>{r.google_name}</div>
                            <div className="text-xs">No longer in Google Ads</div>
                          </td>
                          <td className="px-3 py-2 align-top">{r.local_name}</td>
                          <td className="px-3 py-2 align-top">
                            <Badge variant="secondary" className="text-xs">Archived</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(summary?.unseen_legacy ?? 0) > 0 && (
                <p className="text-xs text-muted-foreground">
                  {summary?.unseen_legacy} campaign{summary?.unseen_legacy === 1 ? '' : 's'} linked to a different Google Ads account were left as they are.
                </p>
              )}

              {unlinkedCampaigns.length > 0 && (
                <div className="space-y-2">
                  <div>
                    <div className="font-medium text-sm">Not linked to Google Ads</div>
                    <p className="text-xs text-muted-foreground">
                      Local campaigns with no Google id. Call-extension, organic and referral numbers belong here.
                      If one is really a Google campaign under another name, merge it: its leads, calls, texts and spend
                      move to the Google row and the duplicate is removed (a backup is kept).
                    </p>
                  </div>
                  <div className="rounded-md border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-medium">Local campaign</th>
                          <th className="px-3 py-2 font-medium w-[90px]">Leads</th>
                          <th className="px-3 py-2 font-medium w-[320px]">Merge into</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unlinkedCampaigns.map((c) => (
                          <tr key={c.id} className="border-t">
                            <td className="px-3 py-2 align-top">
                              <div>{c.name}</div>
                              {c.tracking_number && (
                                <div className="text-xs text-muted-foreground font-mono">{c.tracking_number}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 align-top">{c.lead_count.toLocaleString()}</td>
                            <td className="px-3 py-2 align-top">
                              <div className="flex gap-2">
                                <Select
                                  value={targets[c.id] ?? ''}
                                  onValueChange={(v) => setTargets((prev) => ({ ...prev, [c.id]: v }))}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Keep separate" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {mergeTargets.map((t) => (
                                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!targets[c.id] || isMerging}
                                  onClick={() => setPending(c)}
                                >
                                  <GitMerge className="h-4 w-4 mr-1" /> Merge
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={retrySync} disabled={isSyncing}>
              Sync again
            </Button>
            <Button onClick={() => onOpenChange(false)} disabled={isMerging}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pending !== null} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge "{pending?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Its {pending?.lead_count.toLocaleString()} lead{pending?.lead_count === 1 ? '' : 's'}, calls, texts and
              spend move to "{pending ? targetName(targets[pending.id] ?? '') : ''}" and this campaign is removed.
              Reports for both will change. A backup of the row is kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMerging}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void confirmMerge(); }} disabled={isMerging}>
              {isMerging ? 'Merging…' : 'Merge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
